// The trash bin list — one component, two screens (W39.54).
//
// A soft delete in this product used to mean "gone from every screen, and
// recoverable only if you remembered the id". Wishes and anniversaries both
// grew a trash listing, a restore and a permanent delete on the same day, and
// the rows are the same shape apart from the field the thing is *called*
// (`name` for a wish, `title` for an anniversary) — so the caller normalises
// that and this file stays free of both.
//
// What the component is careful about:
//
//   * **The countdown is the server's.** `daysLeft` arrives computed, next to
//     the cron that will act on it. Recomputing it from `deleted_at` in the
//     browser would be the same mistake ADR-0124 fixed for every other relative
//     time here: naive UTC read as local, eight hours out.
//   * **A row the caller may not restore says so and disables the button**,
//     rather than offering an action that answers 403.
//   * **永久刪除 is a different verb in the UI too** — the row is `delete`d only
//     after a confirm, and the confirm names what is lost.

import { useState } from "react";
import { RotateCcw, Trash2 } from "lucide-react";

import { PageHero } from "@/components/primitives/PageHero";
import { EmptyState } from "@/components/primitives/EmptyState";
import { LoadingState } from "@/components/primitives/LoadingState";
import { PageContainer, SectionStack } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/patterns/ConfirmDialog";
import { useToast } from "@/components/ui/toast";

export interface TrashRow {
  id: string;
  /** Normalised by the caller: a wish's `name`, an anniversary's `title`. */
  label: string;
  /** Server-computed whole days until the retention job removes this. */
  daysLeft: number;
  /** False for a partner's row — visible, not restorable. */
  canRestore: boolean;
}

export interface TrashListProps {
  /** 「心願垃圾桶」 / 「紀念日垃圾桶」 — what is in this bin. */
  noun: string;
  rows: TrashRow[];
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string;
  /** What the row *is*, in the confirm's sentence: 「這個心願」. */
  itemNoun: string;
  /**
   * W45.0 `ADR-0115` — where this bin's parent lives, for the breadcrumb.
   *
   * Required rather than optional: the four call sites each used to end with a
   * sentence of body copy linking back, pinned to the **bottom of the list** —
   * below the fold on any bin with content — and the diary bin, which renders
   * through this shared component, had no link at all. Making it required means
   * a fifth bin cannot be added without one.
   */
  parentTo: string;
  /** The parent module's name — 「心願」 / 「相簿」 / 「紀念日」. */
  parentLabel: string;
  /**
   * The one number per module in one place — see `retentionDays`.
   */
  retentionDays: number;
  onRestore: (id: string) => Promise<void>;
  onPurge: (id: string) => Promise<void>;
  onRetry: () => void;
}

export function TrashList({
  noun,
  rows,
  isLoading,
  isError,
  errorMessage,
  itemNoun,
  parentTo,
  parentLabel,
  retentionDays,
  onRestore,
  onPurge,
  onRetry,
}: TrashListProps) {
  const { push } = useToast();
  const [pendingPurge, setPendingPurge] = useState<TrashRow | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const run = async (id: string, action: () => Promise<void>, okMessage: string) => {
    setBusyId(id);
    try {
      await action();
      push({ variant: "success", message: okMessage });
      return true;
    } catch (err) {
      push({
        variant: "danger",
        message: err instanceof Error ? err.message : "操作未完成，請稍後再試。",
      });
      return false;
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="trash-list">
      <PageHero
        // W45.0 `ADR-0115`: the breadcrumb is the way out of this bin, and it
        // lives at the top where a back control belongs.
        //
        // It replaces a sentence of body copy that each call site pinned to the
        // **bottom of the list** — 「想看還在的心願？回到心願列表」 — which on
        // any bin with content in it sat below the fold, and on the diary bin
        // (which renders through this shared component) was missing entirely.
        // The `eyebrow` is dropped rather than kept beside the trail: a count and
        // a "you are here" are two different lines, and the count is already the
        // subtitle's job on the other bins.
        crumbs={[
          { to: parentTo, label: parentLabel },
          { label: "垃圾桶" },
        ]}
        title={noun}
        // The count used to be the eyebrow (`共 N 項`) and the breadcrumb replaced
        // it — so the number went with it, and W39.78's rule is that a page
        // carries *meaningful* context on every viewport, not merely a heading.
        // The subtitle is where it goes now: it is the same information, on the
        // same line as the retention window it belongs with, and the hero is
        // still a single column of chrome rather than two competing ones.
        subtitle={`共 ${rows.length} 項 · ${retentionDays} 天後自動永久刪除`}
      />

      <PageContainer vertical="default">
        <SectionStack density="default">
          {isLoading ? (
            <LoadingState count={3} />
          ) : isError ? (
            <div className="text-body-sm text-partner-strong" data-testid="trash-error" role="alert">
              {errorMessage ?? "載入失敗"}
              <Button variant="ghost" size="sm" onClick={onRetry} className="ml-s-2">
                重試
              </Button>
            </div>
          ) : rows.length === 0 ? (
            <EmptyState title="垃圾桶是空的" description={`刪掉的東西會在這裡留 ${retentionDays} 天`} />
          ) : (
            <ul className="flex flex-col gap-s-2" data-testid="trash-rows">
              {rows.map((row) => (
                <li
                  key={row.id}
                  data-testid={`trash-row-${row.id}`}
                  // W39.78. The two actions sit in one row with the label, and on
                  // a 393 pt screen that left the countdown about 120 pt: it
                  // wrapped between 刪 and 除 in every bin. The fix is the
                  // layout, not the copy — truncating the countdown would hide
                  // the one number on the row, so on a phone the label gets the
                  // full width and the actions take their own line beneath it.
                  // `sm:` keeps the single-line row where there is room for it.
                  className="flex flex-col gap-s-2 rounded-2xl surface-plain px-s-4 py-s-3 shadow-card sm:flex-row sm:items-center sm:gap-s-3"
                >
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-body text-ink-1">{row.label}</span>
                    <span
                      className="whitespace-nowrap text-body-xs text-ink-3"
                      data-testid={`trash-days-left-${row.id}`}
                    >
                      {row.daysLeft > 0
                        ? `${row.daysLeft} 天後永久刪除`
                        : "即將永久刪除，已無法還原"}
                    </span>
                  </div>
                  <div className="flex items-center gap-s-2 sm:shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      leadingIcon={<RotateCcw className="h-3.5 w-3.5" />}
                      disabled={!row.canRestore || busyId === row.id}
                      onClick={() => void run(row.id, () => onRestore(row.id), "已還原")}
                      data-testid={`trash-restore-${row.id}`}
                    >
                      還原
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      leadingIcon={<Trash2 className="h-3.5 w-3.5" />}
                      // W40.9. This used to read `disabled={!row.canRestore || …}`,
                      // the same condition as 還原 — but the reason for that
                      // condition ("rather than offering an action that answers
                      // 403") is about **restore**, and it does not transfer.
                      // Measured against rows 40 days into a 30-day window, on
                      // all four trash screens:
                      //
                      //   POST   /{res}/{id}/restore     → 410  (diaries, wishes,
                      //                                        anniversaries) / 404
                      //                                        (albums) — refused
                      //   DELETE /{res}/{id}/permanent   → 204  — **allowed**
                      //
                      // So an expired row was a dead end: the copy said 「即將永久
                      // 刪除，已無法還原」, both buttons were disabled, and the
                      // person had to wait for the 03:00 cron to do the one thing
                      // the server would have done on request. 還原 stays
                      // disabled — that is the 403 the rule exists to prevent —
                      // and 永久刪除 stays available, because it is what the
                      // user is actually looking at.
                      disabled={busyId === row.id}
                      onClick={() => setPendingPurge(row)}
                      data-testid={`trash-purge-${row.id}`}
                      className="text-partner-strong"
                    >
                      永久刪除
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </SectionStack>
      </PageContainer>

      <ConfirmDialog
        open={pendingPurge !== null}
        onOpenChange={(open) => {
          if (!open) setPendingPurge(null);
        }}
        title={`永久刪除這個${itemNoun}？`}
        // Scoped copy, and the distinction is the whole point: the soft delete
        // is what 「刪除」 does everywhere else in this product, and this is the
        // one that cannot be undone. Saying nothing would put an irreversible
        // action one tap away from a reversible one.
        //
        // W40.9. The 「${retentionDays} 天內都還能還原」 half is only true while
        // the window is open. On a row that has already passed it, that sentence
        // tells a person they can close the dialog and get it back, which is the
        // one thing they cannot do — and ADR-0079/0080 exist because this
        // product does not state things that are not true. So the two cases get
        // two descriptions rather than one optimistic one.
        description={
          pendingPurge && !pendingPurge.canRestore
            ? "這個動作無法復原，刪除後就找不回來了。還原時間已經過了，現在只能選擇保留到自動清除，或立即刪除。"
            : `這個動作無法復原，刪除後就找不回來了。如果只是先移出畫面，請關閉這個視窗，${retentionDays} 天內都還能還原。`
        }
        confirmLabel="永久刪除"
        cancelLabel="取消"
        destructive
        onConfirm={async () => {
          const row = pendingPurge;
          setPendingPurge(null);
          if (!row) return;
          await run(row.id, () => onPurge(row.id), "已永久刪除");
        }}
      />
    </div>
  );
}
